import { z } from "zod";
import { Finite, IsoDate, Symbol_ } from "./primitives";
import {
  EquityPoint,
  FlagTolerance,
  IsoMonth,
  MAX_EQUITY_POINTS,
  MAX_MONTHS,
  MAX_POSITIONS,
  MAX_SKIPS,
  PersonaPosition,
  PersonaSkip,
  PersonaSlug,
  TradeSide,
} from "./persona";
import { PersonaStyle } from "./persona";
import { SimTradeRecord, VpCadence } from "./sim";
import { Weights } from "./scoring";

/*
 * Virtual portfolios: the same simulation the personas run, owned by a user.
 *
 * Everything here shares the personas' storage discipline — document ids
 * double as partition keys so every access is a point read or upsert at about
 * 1 RU, and nothing is ever discovered by querying the container. That is what
 * keeps the free tier's 400 RU/s a flat cost rather than one that grows with
 * the number of users.
 *
 * Two index arrays exist purely to preserve that: `summaries` on the per-user
 * root (so "list my portfolios" is one read, not 1+N) and `months` on each
 * portfolio (so "which ledgers exist" is a field, not a query). Both are the
 * same trick PersonaPortfolioDocument.months already uses.
 */

export const VP_SCHEMA_VERSION = 1;

/**
 * The identity provider's user id, constrained before it is concatenated into
 * a document id.
 *
 * The portfolio store's usual argument — that a caller cannot address someone
 * else's document because the key never comes from the request — is not quite
 * enough here, because the key is now built by joining `vp`, the user id and a
 * portfolio id with colons. A principal containing a colon could forge a
 * neighbouring id. The request is rejected rather than the character escaped:
 * every real provider issues ids well inside this alphabet, so a violation is
 * a signal, not an inconvenience.
 */
export const UserId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, "unsupported user id characters");

/** Server-generated, never accepted from a client. */
export const PortfolioId = z.string().regex(/^[a-z0-9]{8,12}$/, "expected a generated portfolio id");

export const vpRootId = (userId: string): string => `vp:${userId}`;
export const vpDocId = (userId: string, portfolioId: string): string => `vp:${userId}:${portfolioId}`;
export const vpLedgerId = (userId: string, portfolioId: string, month: string): string =>
  `vp:${userId}:${portfolioId}:${month}`;

/**
 * The sweep's enumeration index, and the leaderboard's input set.
 *
 * One document today. The shard suffix is in the id from the start so that
 * splitting it later is a constant change rather than a data migration.
 */
export const ROSTER_SHARDS = 1;
export const VP_ROSTER_ID = "vp:roster:0";
export const VP_LEADERBOARD_ID = "vp:leaderboard";

export const MAX_VPORTFOLIOS_PER_USER = 5;
/** Each manual trade is a real Cosmos write, unlike a same-day re-run. */
export const MAX_MANUAL_TRADES_PER_DAY = 20;
export const MAX_VP_LEDGER_TRADES = 300;
export const MAX_SETTINGS_HISTORY = 50;
export const MAX_ROSTER_ENTRIES = 2000;
export const MAX_LEADERBOARD_ROWS = 100;
export const MIN_STARTING_CAPITAL = 1000;
export const MAX_STARTING_CAPITAL = 1_000_000;

/**
 * How a portfolio trades.
 *
 * `hybrid` is `auto` plus the right to trade by hand; the engine treats them
 * identically and only the manual-trade endpoint distinguishes them.
 */
export const VpMode = z.enum(["manual", "auto", "hybrid"]);
export type VpMode = z.infer<typeof VpMode>;

/**
 * The rules a portfolio trades by — the user-editable equivalent of a
 * PersonaDefinition, minus the identity fields.
 *
 * `positions` stops at 15 rather than the document's 25 so that a target set
 * plus in-flight manual positions cannot push past the storage cap.
 */
export const VpSettings = z.object({
  weights: Weights,
  positions: z.number().int().min(1).max(15),
  leverage: Finite.min(0.5).max(1.5),
  bankFloor: Finite.nonpositive(),
  cadence: VpCadence,
  flagTolerance: FlagTolerance,
});
export type VpSettings = z.infer<typeof VpSettings>;

/**
 * Where a portfolio's decisions come from.
 *
 * Both variants carry `settings`, so the engine can always read one shape. For
 * a mirror they are a copy of the persona's rules taken at creation: the
 * cadence is forced to `manual` because the mirror step supplies the trades,
 * and the rest only supplies the score context stamped on each ledger row.
 */
export const VpStrategy = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("settings"),
    /** The persona these settings were copied from, if any. Display only. */
    basedOn: PersonaSlug.nullable(),
    settings: VpSettings,
  }),
  z.object({
    kind: z.literal("mirror"),
    personaSlug: PersonaSlug,
    /**
     * userStartingCapital / personaStartingCapital, fixed at creation.
     *
     * Fixed rather than recomputed from equity because a recomputed ratio is
     * path-dependent: it cannot reproduce an exit cleanly, and it drifts into
     * either hoarding cash or attempting buys the bank cannot cover.
     */
    ratio: Finite.positive(),
    settings: VpSettings,
  }),
]);
export type VpStrategy = z.infer<typeof VpStrategy>;

/**
 * A hand-entered trade, before the server has priced or validated it.
 *
 * Sized by share count, by dollar amount, or by "all of it" — because the rest
 * of the system sizes in dollars and only the server knows the price.
 *
 * The rule engine allocates capital: `slice = leverage × equity / positions`,
 * then converts at the close. Equal weighting is inherently a dollar idea, so
 * requiring a share count by hand made the manual path think in units the rest
 * of the feature never uses. Worse, share counts come out fractional to four
 * decimals — a position of 2.5993 shares could not be closed without
 * transcribing that number exactly, and nothing on the page displayed it.
 */
export const VpManualTradeRequest = z
  .object({
    side: TradeSide,
    symbol: Symbol_,
    shares: Finite.positive().optional(),
    /** Dollars to spend or raise. Converted at the close, server-side. */
    value: Finite.positive().optional(),
    /** Sell the whole holding. Sells only; the exact size is the server's. */
    all: z.boolean().optional(),
  })
  .refine((t) => [t.shares, t.value, t.all].filter((v) => v !== undefined).length === 1, {
    message: "give exactly one of shares, value, or all",
  })
  .refine((t) => !t.all || t.side === "sell", {
    message: "`all` only makes sense when selling",
    path: ["all"],
  });
export type VpManualTradeRequest = z.infer<typeof VpManualTradeRequest>;

/**
 * One virtual portfolio.
 *
 * The trading state is deliberately field-for-field identical to
 * PersonaPortfolioDocument, because the same engine writes both. What differs
 * is ownership (`userId`, `portfolioId`), intent (`label`, `mode`,
 * `strategy`), and the two things only a user portfolio needs: a record of
 * every settings change, and a daily manual-trade counter.
 *
 * Note the absence of a run counter. A same-day re-run is two point reads and
 * no write, and persisting a count would force a write onto exactly the path
 * whose virtue is being free.
 */
export const VPortfolioDocument = z
  .object({
    id: z.string().min(1),
    docType: z.literal("vportfolio"),
    schemaVersion: z.literal(VP_SCHEMA_VERSION),
    userId: UserId,
    portfolioId: PortfolioId,
    label: z.string().min(1).max(60),
    mode: VpMode,
    strategy: VpStrategy,
    startingCapital: Finite.min(MIN_STARTING_CAPITAL).max(MAX_STARTING_CAPITAL),
    /** Opted in to the cross-user leaderboard. Never defaulted to true. */
    listed: z.boolean(),

    bank: Finite,
    startedAsOf: IsoDate,
    lastTradedAsOf: IsoDate.nullable(),
    lastRebalanceAsOf: IsoDate.nullable(),
    positions: z.array(PersonaPosition).max(MAX_POSITIONS),
    realizedPnlTotal: Finite,
    equityHistory: z.array(EquityPoint).max(MAX_EQUITY_POINTS),
    months: z.array(IsoMonth).max(MAX_MONTHS),
    skips: z.array(PersonaSkip).max(MAX_SKIPS),

    /**
     * Every settings change, newest last, oldest dropped past the cap.
     *
     * Kept because a re-tuned portfolio's equity curve is not one strategy's
     * result, and a leaderboard that did not say so would be misleading.
     */
    settingsHistory: z
      .array(z.object({ changedAt: z.string(), settings: VpSettings }))
      .max(MAX_SETTINGS_HISTORY),
    /**
     * Manual trades used today. `asOf` is a UTC calendar day, so the allowance
     * rolls over at midnight UTC rather than in the owner's timezone.
     */
    manualTrades: z.object({ asOf: IsoDate, count: z.number().int().nonnegative() }),
    updatedAt: z.string(),
  })
  .refine((d) => d.strategy.kind !== "mirror" || !d.listed, {
    message: "a mirror portfolio cannot be listed on the leaderboard",
    path: ["listed"],
  })
  .refine((d) => d.strategy.settings.bankFloor >= maxBorrowing(d.startingCapital), {
    message: "bankFloor may not exceed half the starting capital in borrowing",
    path: ["strategy", "settings", "bankFloor"],
  });
export type VPortfolioDocument = z.infer<typeof VPortfolioDocument>;

/** The most negative a bank may go, for a given starting capital. */
export const maxBorrowing = (startingCapital: number): number => -0.5 * startingCapital;

/**
 * Brings a borrowing limit inside what this portfolio's capital allows.
 *
 * Necessary because the limit is capital-relative and the personas are not:
 * Max's floor is −$100,000 against his own $3,000, which is a borrowing line
 * 33× his capital. Copying his settings verbatim into a user portfolio
 * produces a document the schema refuses — so the value is clamped where the
 * capital is known rather than validated somewhere it is not.
 *
 * Clamped rather than rejected: a preset is something the user picked from a
 * list, not a number they typed, and refusing it would leave them with no way
 * to proceed since the editor does not expose this field.
 */
export const clampBankFloor = (bankFloor: number, startingCapital: number): number =>
  Math.max(bankFloor, maxBorrowing(startingCapital));

/** What the list view needs, denormalized so listing costs one point read. */
export const VPortfolioSummary = z.object({
  portfolioId: PortfolioId,
  label: z.string().min(1).max(60),
  mode: VpMode,
  strategyKind: z.enum(["settings", "mirror"]),
  listed: z.boolean(),
  startingCapital: Finite,
  equity: Finite,
  returnPct: Finite,
  lastTradedAsOf: IsoDate.nullable(),
});
export type VPortfolioSummary = z.infer<typeof VPortfolioSummary>;

/**
 * One user's root document: their leaderboard handle and the index of their
 * portfolios.
 *
 * `displayName` is null until the user types one. It is never derived from the
 * principal, because `userDetails` is an email address or a GitHub login and
 * publishing that on a cross-user board would leak it.
 */
export const VPortfolioRoot = z.object({
  id: z.string().min(1),
  docType: z.literal("vpRoot"),
  schemaVersion: z.literal(VP_SCHEMA_VERSION),
  userId: UserId,
  displayName: z
    .string()
    .min(3)
    .max(24)
    .regex(/^[A-Za-z0-9 _-]+$/, "letters, digits, spaces, hyphens and underscores only")
    .nullable(),
  summaries: z.array(VPortfolioSummary).max(MAX_VPORTFOLIOS_PER_USER),
  updatedAt: z.string(),
});
export type VPortfolioRoot = z.infer<typeof VPortfolioRoot>;

/** One month of one portfolio's trades. Sharded by month like the personas'. */
export const VPortfolioLedger = z.object({
  id: z.string().min(1),
  docType: z.literal("vpLedger"),
  schemaVersion: z.literal(VP_SCHEMA_VERSION),
  userId: UserId,
  portfolioId: PortfolioId,
  month: IsoMonth,
  trades: z.array(SimTradeRecord).max(MAX_VP_LEDGER_TRADES),
  updatedAt: z.string(),
});
export type VPortfolioLedger = z.infer<typeof VPortfolioLedger>;

/**
 * Every portfolio in existence, so the nightly sweep can enumerate without a
 * container query.
 *
 * A derivable cache, not a source of truth: it lives in a different partition
 * from the portfolios it names, so no write can span both. The sweep drops
 * entries whose portfolio has gone, and a run repairs a missing entry.
 */
export const VPortfolioRoster = z.object({
  id: z.string().min(1),
  docType: z.literal("vpRoster"),
  schemaVersion: z.literal(VP_SCHEMA_VERSION),
  entries: z
    .array(
      z.object({
        userId: UserId,
        portfolioId: PortfolioId,
        mode: VpMode,
        listed: z.boolean(),
      }),
    )
    .max(MAX_ROSTER_ENTRIES),
  updatedAt: z.string(),
});
export type VPortfolioRoster = z.infer<typeof VPortfolioRoster>;

/**
 * One standing.
 *
 * Ranked on percentage return because starting capital is the owner's choice,
 * so dollars are not comparable. `tunedCount` and `days` are shown alongside:
 * a portfolio re-tuned six times over four days is not the same claim as one
 * left alone for six months.
 */
export const LeaderboardRow = z.object({
  rank: z.number().int().positive(),
  displayName: z.string().min(1).max(24),
  label: z.string().min(1).max(60),
  returnPct: Finite,
  equity: Finite,
  startingCapital: Finite,
  mode: VpMode,
  basedOn: PersonaSlug.nullable(),
  tunedCount: z.number().int().nonnegative(),
  startedAsOf: IsoDate,
  days: z.number().int().nonnegative(),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRow>;

export const LeaderboardDocument = z.object({
  id: z.string().min(1),
  docType: z.literal("vpLeaderboard"),
  schemaVersion: z.literal(VP_SCHEMA_VERSION),
  asOfDate: IsoDate,
  rows: z.array(LeaderboardRow).max(MAX_LEADERBOARD_ROWS),
  updatedAt: z.string(),
});
export type LeaderboardDocument = z.infer<typeof LeaderboardDocument>;

// --- request bodies ---------------------------------------------------------
// Each carries no identity of its own: who is asking comes from the session,
// and which portfolio from the route, so there is nothing here to spoof.

export const VpCreateRequest = z.object({
  label: z.string().min(1).max(60),
  mode: VpMode,
  startingCapital: Finite.min(MIN_STARTING_CAPITAL).max(MAX_STARTING_CAPITAL),
  strategy: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("settings"),
      basedOn: PersonaSlug.nullable(),
      settings: VpSettings,
    }),
    z.object({ kind: z.literal("mirror"), personaSlug: PersonaSlug }),
  ]),
});
export type VpCreateRequest = z.infer<typeof VpCreateRequest>;

export const VpPatchRequest = z
  .object({
    label: z.string().min(1).max(60).optional(),
    mode: VpMode.optional(),
    listed: z.boolean().optional(),
    /**
     * The whole vector, always. `Weights` carries a sum-to-one refinement, so
     * it cannot be made partial — and a half-applied weight change would be a
     * portfolio trading rules nobody chose.
     */
    settings: VpSettings.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: "nothing to change",
  });
export type VpPatchRequest = z.infer<typeof VpPatchRequest>;

export const VpRootPatchRequest = z.object({
  displayName: z
    .string()
    .min(3)
    .max(24)
    .regex(/^[A-Za-z0-9 _-]+$/, "letters, digits, spaces, hyphens and underscores only")
    .nullable(),
});
export type VpRootPatchRequest = z.infer<typeof VpRootPatchRequest>;

// --- responses --------------------------------------------------------------

/** A portfolio as the browser sees it: stored state plus derived value. */
export const VPortfolioView = z.object({
  portfolioId: PortfolioId,
  label: z.string(),
  mode: VpMode,
  strategy: VpStrategy,
  startingCapital: Finite,
  listed: z.boolean(),
  bank: Finite,
  positions: z.array(PersonaPosition.extend({ marketValue: Finite, unrealizedPnl: Finite })),
  positionsValue: Finite,
  equity: Finite,
  realizedPnlTotal: Finite,
  unrealizedPnl: Finite,
  returnPct: Finite,
  startedAsOf: IsoDate,
  lastTradedAsOf: IsoDate.nullable(),
  equityHistory: z.array(EquityPoint),
  recentTrades: z.array(SimTradeRecord),
  months: z.array(IsoMonth),
  skips: z.array(PersonaSkip),
  tunedCount: z.number().int().nonnegative(),
  manualTradesLeftToday: z.number().int().nonnegative(),
});
export type VPortfolioView = z.infer<typeof VPortfolioView>;

export const VPortfolioListResponse = z.object({
  displayName: z.string().nullable(),
  summaries: z.array(VPortfolioSummary),
  maxPortfolios: z.number().int().positive(),
});
export type VPortfolioListResponse = z.infer<typeof VPortfolioListResponse>;

export const LeaderboardResponse = z.object({
  asOfDate: IsoDate.nullable(),
  rows: z.array(LeaderboardRow),
  updatedAt: z.string().nullable(),
});
export type LeaderboardResponse = z.infer<typeof LeaderboardResponse>;

/**
 * The tuning and persona presets the API needs, bundled beside the function.
 *
 * Managed functions cannot read the static app's files and have no business
 * parsing YAML at request time, so config/personas.yaml is projected into this
 * at build time. The persona settings here are what a new portfolio copies
 * when it is created from a preset or set to mirror.
 */
export const SimConfigArtifact = z.object({
  startingCapital: Finite.positive(),
  minTradeValue: Finite.positive(),
  missingGraceDays: z.number().int().positive(),
  thinSnapshotFloor: Finite.min(0).max(1),
  deterioration: z.object({ scoreDrop: Finite.positive(), rankFloorMultiple: Finite.positive() }),
  personas: z.array(
    z.object({
      slug: PersonaSlug,
      name: z.string().min(1),
      style: PersonaStyle,
      tagline: z.string().max(200),
      settings: VpSettings,
      /**
       * True for a persona whose rules cannot be copied into a user portfolio
       * (an intraday cadence). Its trades can still be mirrored; `settings`
       * then carries a `manual` cadence, as every mirror does. Optional so a
       * sim-config built before the flag existed still parses.
       */
      followOnly: z.boolean().optional(),
    }),
  ),
});
export type SimConfigArtifact = z.infer<typeof SimConfigArtifact>;
